import { useEffect, useRef, useState } from 'react';
import { Box, IconButton, InputLabel, Typography } from '@mui/material';
import classNames from 'classnames/bind';
import { useTranslation } from 'react-i18next';
import Map, { MapRef, Marker, NavigationControl } from 'react-map-gl/mapbox';
import dashboardApi from '~/apis/dashboard';
import { CancelIcon, CommunityLineIcon, MapPinFill2, MapPinLine, PhoneFillIcon } from '~/assets/icons';
import { IInstallationLocationItem } from '~/utils/interface/dashboard';
import styles from '../Dashboard.module.scss';
import { handleApiError } from '~/utils/helper';
import BaseRow from '~/components/common/base-row/BaseRow';
const cx = classNames.bind(styles);

function MapBox() {
  const { t } = useTranslation('admin/home');

  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [openDescription, setOpenDescription] = useState<boolean>(false);
  const [locations, setLocations] = useState<IInstallationLocationItem[]>([]);
  const mapRef = useRef<MapRef | null>(null);
  useEffect(() => {
    (async () => {
      try {
        const response = await dashboardApi.getListInstallationLocation();
        setLocations(response.data);
      } catch (error) {
        handleApiError(error);
      }
    })();
  }, []);
  const zoomTo = (lat: number, long: number) => {
    setOpenDescription(true);
    const map = mapRef.current?.getMap();
    if (!map) return;

    map.flyTo({
      center: [long, lat],
      zoom: 15,
      speed: 1,
      curve: 1.4,
      essential: true,
    });
  };

  const onMapLoad = () => {
    const map = mapRef.current?.getMap();
    if (!map) return;

    const layers = map.getStyle().layers;

    const seaLabelLayer = layers.find((layer) => layer.id === 'water-label-sm' || layer.id === 'water-label-md');

    if (seaLabelLayer) {
      map.setFilter(seaLabelLayer.id, ['==', ['get', 'name_en'], 'South China Sea']);
      map.setLayoutProperty(seaLabelLayer.id, 'text-field', 'Biển Đông');
    }

    for (const layer of layers) {
      if (layer.type === 'symbol' && layer.layout && layer.layout['text-field']) {
        if (layer.id.endsWith('-label')) {
          map.setLayoutProperty(layer.id, 'text-field', ['coalesce', ['get', `name_vi`], ['get', 'name_en']]);
        }
      }
    }
  };

  const renderColorByLocationStatus = (locationStatus: number) => {
    if (locationStatus === 1) return '#02AB75';
    if (locationStatus === 2) return '#ED1002';
    if (locationStatus === 3) return '#FF9500';
    if (locationStatus === 4) return '#72777B';
    return '';
  };

  const renderLocationIcon = (item: any) => {
    return (
      <MapPinFill2
        color={renderColorByLocationStatus(item.location_status)}
        onClick={() => {
          zoomTo(item.latitude, item.longitude);
          setSelectedItem(item);
        }}
      />
    );
  };
  return (
    <Box className={cx('w-[40%] h-full', 'dashboard-widget')}>
      <Typography className={cx('dashboard-widget-title', 'dashboard-widget-title-text')}>{t('facility.title')}</Typography>
      <Box className={cx('p-5', 'dashboard-widget-body')}>
        <Box className={cx('total-facility')}>
          <BaseRow className='gap-1 my-1.5 !items-center'>
            <Box className='w-1/4 mr-1 text-end font-bold text-[#ED1002]'>{locations?.filter((location) => location.location_status === 2).length}</Box>
            <InputLabel className='w-full'>{t('facility.alert')}</InputLabel>
          </BaseRow>
          <BaseRow className='gap-1 mb-1.5 !items-center'>
            <Box className='w-1/4 mr-1 text-end font-bold text-[#02AB75]'>{locations?.filter((location) => location.location_status === 1).length}</Box>
            <InputLabel className='w-full'>{t('facility.normal')}</InputLabel>
          </BaseRow>
          <BaseRow className='gap-1 mb-1.5 !items-center'>
            <Box className='w-1/4 mr-1 text-end font-bold text-[#FF9500]'>{locations?.filter((location) => location.location_status === 3).length}</Box>
            <InputLabel className='w-full'>{t('facility.warning')}</InputLabel>
          </BaseRow>
          <BaseRow className='gap-1 mb-1.5 !items-center'>
            <Box className='w-1/4 mr-1 text-end font-bold text-[#72777B]'>{locations?.filter((location) => location.location_status === 4).length}</Box>
            <InputLabel className='w-full'>{t('facility.disconnect')}</InputLabel>
          </BaseRow>
        </Box>
        <Box className='w-full h-full border border-[#DFDFDF]'>
          <Map
            ref={mapRef}
            mapboxAccessToken={process.env.REACT_APP_MAP_BOX_ACCESS_TOKEN}
            initialViewState={{
              longitude: 105.85,
              latitude: 21.02,
              zoom: 6,
            }}
            style={{ width: '100%', height: '100%' }}
            mapStyle='mapbox://styles/mapbox/streets-v12'
            onLoad={onMapLoad}
          >
            <NavigationControl position='top-right' />
            {locations?.map((item) => {
              return (
                <Marker longitude={item.longitude} latitude={item.latitude} anchor='bottom' key={item.location_id}>
                  {renderLocationIcon(item)}
                </Marker>
              );
            })}

            {openDescription && (
              <Box
                sx={{
                  position: 'absolute',
                  bottom: 20,
                  left: 20,
                  background: 'rgba(255, 255, 255, 0.9)',
                  padding: '10px 14px',
                  borderRadius: '8px',
                  boxShadow: '0 2px 6px rgba(0,0,0,0.2)',
                  fontSize: '14px',
                  width: '300px',
                  overflow: 'hidden',
                }}
              >
                <Box className='flex items-center gap-2'>
                  <CommunityLineIcon fill='#005F73' />
                  <Typography className='font-bold text-[#ED1002]'>{selectedItem.location_name}</Typography>
                </Box>
                <Box className='flex items-center gap-2 mt-2'>
                  <PhoneFillIcon fill='#005F73' />
                  <Typography>{selectedItem.manager_phone}</Typography>
                </Box>
                <Box className='flex items-center gap-2 mt-2'>
                  <MapPinLine className='w-[25px] h-[25px]' fill='#ED1002' />
                  <Typography>{selectedItem.address}</Typography>
                </Box>
                <IconButton
                  className='absolute top-1 right-2'
                  onClick={() => {
                    setOpenDescription(false);
                  }}
                >
                  <CancelIcon fill='#005F73' className='w-[14px] h-[14px]' />
                </IconButton>
              </Box>
            )}
          </Map>
        </Box>
      </Box>
    </Box>
  );
}

export default MapBox;
